import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, CheckCircle2, LoaderCircle } from 'lucide-react';
import DropboxIcon from '../../icons/dropbox';
import { checkDropboxConnection, disconnectDropbox } from '../../service/apiService';

// Declare global google object for TypeScript
declare global {
  interface Window {
    google: any;
  }
}

interface DropboxAuthProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (code: string) => void;
}

const DropboxAuth: React.FC<DropboxAuthProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
}) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [lastAction, setLastAction] = useState<'connect' | 'disconnect' | null>(null);
  const codeClientRef = useRef<any>(null);

  // Google OAuth configuration 
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID || "";
  const GOOGLE_SCOPE = 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/presentations';

  useEffect(() => {
    if (!isOpen) {
      setAuthError(null);
      setAuthSuccess(false);
      setIsAuthenticating(false);
      setIsDisconnecting(false);
      setLastAction(null);
    } else {
      // Check connection status when modal opens
      checkConnectionStatus();
    }
  }, [isOpen]);

  const checkConnectionStatus = async () => {
    setCheckingConnection(true);
    try {
      const connected = await checkDropboxConnection();
      setIsConnected(connected);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    // Initialize Google Identity Services client when component mounts
    if (window.google && !codeClientRef.current) {
      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_SCOPE,
        ux_mode: 'popup',
        access_type: 'offline',
        prompt: 'consent',
        callback: (response: any) => {
          console.log('Full OAuth response:', response);
          console.log('Requested scope:', GOOGLE_SCOPE);
          console.log('Granted scope:', response.scope);
          
          if (response.code) {
            setIsAuthenticating(false);
            setAuthSuccess(true);
            setLastAction('connect'); 
            onAuthSuccess(response.code);
            setTimeout(() => {
              checkConnectionStatus();
              setAuthSuccess(false);
            }, 1500);
          } else {
            setIsAuthenticating(false);
            setAuthError('Authentication failed');
          }
        }
      });
    }
  }, [GOOGLE_CLIENT_ID, GOOGLE_SCOPE, onAuthSuccess, onClose]);

  const handleGoogleSignIn = () => {
  console.log('Connect button clicked');
  setLastAction('connect'); 
  
  if (!window.google) {
    setAuthError('Google Identity Services not loaded');
    return;
  }

  if (!GOOGLE_CLIENT_ID) {
    setAuthError('Google Client ID not configured');
    return;
  }

  setIsAuthenticating(true);
  setAuthError(null);

  try {
    codeClientRef.current?.requestCode();
  } catch (error) {
    setIsAuthenticating(false);
    setAuthError('Failed to initiate Google authentication');
    console.error('Google auth error:', error);
  }
};

const handleDisconnect = async () => {
  console.log('Disconnect button clicked');
  setLastAction('disconnect');
  
  setIsDisconnecting(true);
  setAuthError(null);

  try {
    console.log('Calling disconnectDropbox API...');
    const result = await disconnectDropbox();
    console.log('API response:', result);
    
    if (result.success) {
      console.log('Disconnect successful!');
      setIsConnected(false);
      setAuthSuccess(true);
      
      setTimeout(() => {
        console.log('Closing modal after successful disconnect');
        onClose();
      }, 1500);
    } else {
      console.log('Disconnect failed with error:', result.error);
      setAuthError(result.error || 'Failed to disconnect Dropbox account');
    }
  } catch (error) {
    console.error('Exception in disconnect:', error);
    setAuthError('Failed to disconnect Dropbox account. Please try again.');
  } finally {
    console.log('Disconnect process completed');
    setIsDisconnecting(false);
  }
};
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <DropboxIcon size={18} className="mr-2" />
            Dropbox Connection
          </h3>
          <button
            onClick={() => {
              setIsAuthenticating(false);
              setIsDisconnecting(false); 
              onClose();
           }}
           className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
          >
        <X size={20} />
       </button>
        </div>

        {checkingConnection ? (
          <div className="text-center py-8">
            <LoaderCircle className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Verifying connection...</p>
          </div>
        ) : authSuccess ? (
  <div className="text-center py-8">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
      <CheckCircle2 className="text-green-600" size={24} />
    </div>
    <h4 className="text-base font-semibold text-gray-900 mb-1">
      {lastAction === 'connect' ? 'Connected Successfully' : 'Disconnected Successfully'}
    </h4>
    <p className="text-sm text-gray-600">
      {lastAction === 'connect'
        ? 'Your Dropbox account is now connected.' 
        : 'Your Dropbox account has been removed.'
      }
    </p>
  </div>
        ) : (
  <>
    {isConnected && (
      <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
        <div className="flex items-center">
          <CheckCircle2 className="text-green-600 mr-2 flex-shrink-0" size={16} />
          <span className="text-sm text-green-800">Account connected</span>
        </div>
      </div>
    )}

    {authError && (
      <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
        <div className="flex items-start">
          <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-sm text-red-700">{authError}</p>
        </div>
      </div>
    )}

    <div className="flex gap-3">
      <button
        onClick={handleGoogleSignIn}
        disabled={isAuthenticating || isDisconnecting}
        className="flex-1 bg-violet-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isAuthenticating ? (
          <>
            <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
            <span>Connecting...</span>
          </>
        ) : (
          'Connect Account'
        )}
      </button>
      
      <button
        onClick={handleDisconnect}
        disabled={isAuthenticating || isDisconnecting || !isConnected}
        className="flex-1 bg-white text-gray-700 py-2.5 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isDisconnecting ? (
          <>
            <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
            <span>Disconnecting...</span>
          </>
        ) : (
          'Disconnect'
        )}
      </button>
    </div>

    <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
      {isConnected 
        ? 'Disconnect to revoke Kyva\'s access to your Dropbox.'
        : 'Connect your account to access Dropbox files, documents, sheets, and presentations.'
      }
    </p>
  </>
        )}

        <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
          By connecting, you authorize Kyva to access your Dropbox files.
        </p>
      </div>
    </div>
  );
};

export default DropboxAuth;